// Shared same-origin JSON fetch for the live chat and live tip endpoints: CSRF header, JSON in
// and out, and one error type that carries the HTTP status, the parsed body and Retry-After.

export class RequestError extends Error {
  constructor(message, { status = 0, retryAfterMs = null, payload = null } = {}) {
    super(message);
    this.name = "RequestError";
    this.status = status;
    this.retryAfterMs = retryAfterMs;
    this.payload = payload;
  }
}

export function csrfToken() {
  const token = document.querySelector('meta[name="csrf-token"]');
  return token ? token.content : "";
}

// Resolves with { ok, status, payload, retryAfterMs } for ANY HTTP status carrying a JSON body -
// these endpoints return structured JSON for every expected outcome, so the caller decides what a
// 4xx means. Throws RequestError for a network failure or a non-JSON body (an auth-redirect HTML
// page, a 500 page), which break that contract.
export async function requestJson(url, { method = "POST", body, signal } = {}) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    throw new RequestError("network_error", { status: 0 });
  }

  const retryAfterMs = parseRetryAfter(response.headers.get("Retry-After"));
  if (response.status === 204) return { ok: true, status: 204, payload: null, retryAfterMs };

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new RequestError("non_json_response", { status: response.status, retryAfterMs });
  }

  const payload = await response.json();
  return { ok: response.ok, status: response.status, payload, retryAfterMs };
}

// Resolves with the parsed body on 2xx and rejects with a RequestError (status, payload,
// retryAfterMs attached) otherwise, for callers that treat every non-2xx as a failure.
export async function postJson(url, body) {
  return unwrap(await requestJson(url, { method: "POST", body }));
}

export async function deleteJson(url, body) {
  return unwrap(await requestJson(url, { method: "DELETE", body }));
}

function unwrap(result) {
  if (result.ok) return result.payload;

  const message = (result.payload && result.payload.error) || `http_${result.status}`;
  throw new RequestError(String(message), { status: result.status, retryAfterMs: result.retryAfterMs, payload: result.payload });
}

// Retry-After is either delay-seconds or an HTTP date; null when absent or unparseable.
function parseRetryAfter(header) {
  if (!header) return null;

  const seconds = Number(header);
  if (!Number.isNaN(seconds)) return Math.max(0, seconds * 1000);

  const at = Date.parse(header);
  return Number.isNaN(at) ? null : Math.max(0, at - Date.now());
}
